import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { projectService, caseService } from '../services/api';
import CreateProjectModal from '../components/CreateProjectModal';

const Projects = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const canCreateProject = currentUser?.role === 'administrator' || currentUser?.role === 'instructor';
  const canManageProject = currentUser?.role === 'administrator' || currentUser?.role === 'instructor';

  useEffect(() => {
    fetchProjects();
  }, [includeArchived]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects(includeArchived);
      
      // Fetch case counts for each project
      const projectsWithCaseCounts = await Promise.all(
        data.map(async (project) => {
          try {
            const cases = await caseService.getCases(project.id);
            return { ...project, cases_count: cases.length };
          } catch (error) {
            console.error(`Error fetching cases for project ${project.id}:`, error);
            return { ...project, cases_count: 0 };
          }
        })
      );
      
      setProjects(projectsWithCaseCounts);
    } catch (error) {
      toast.error(t('projects.fetchProjectsError'));
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (projectData) => {
    try {
      await projectService.createProject(projectData);
      setShowCreateModal(false);
      fetchProjects();
    } catch (error) {
      toast.error(t('workspaces.createError'));
      console.error('Error creating workspace:', error);
    }
  };

  const handleArchiveWorkspace = async (workspaceId, isArchived) => {
    const action = isArchived ? 'unarchive' : 'archive';
    const confirmMessage = isArchived 
      ? t('workspaces.confirmUnarchive')
      : t('workspaces.confirmArchive');
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await projectService.updateProject(workspaceId, { is_archived: !isArchived });
      
      // If archiving and not currently showing archived items, enable the filter
      if (!isArchived && !includeArchived) {
        setIncludeArchived(true);
        toast.info(t('workspaces.showingArchived'));
        // Fetch with archived included immediately
        const data = await projectService.getProjects(true);
        setProjects(data);
      } else {
        fetchProjects();
      }
    } catch (error) {
      toast.error(t(`workspaces.${action}Error`));
      console.error(`Error ${action}ing workspace:`, error);
    }
  };

  const handleDeleteWorkspace = async (workspaceId, workspaceName) => {
    if (!window.confirm(t('workspaces.confirmDelete', { name: workspaceName }))) {
      return;
    }
    
    if (!window.confirm(t('workspaces.confirmDeleteFinal'))) {
      return;
    }

    try {
      await projectService.deleteProject(workspaceId);
      fetchProjects();
    } catch (error) {
      toast.error(t('workspaces.deleteError'));
      console.error('Error deleting workspace:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-neutral-900">{t('workspaces.title')}</h1>
          <p className="mt-1 text-sm text-neutral-600">
            {t('workspaces.subtitle')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {canCreateProject && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 transition-colors"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('workspaces.create')}
            </button>
          )}
        </div>
      </div>

      {/* Filters and Stats */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
            />
            <span className="ml-2 text-sm text-neutral-700">{t('workspaces.includeArchived')}</span>
          </label>
        </div>
        
        {projects.length > 0 && (
          <div className="text-sm text-neutral-500">
            {t('workspaces.showingCount', { count: projects.length })}
          </div>
        )}
      </div>

      {/* Workspaces Grid */}
      {loading ? (
        <div className="mt-8 flex justify-center">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
            <p className="mt-4 text-neutral-500">{t('common.loading')}</p>
          </div>
        </div>
      ) : projects.length === 0 ? (
        <div className="mt-12 text-center">
          <svg className="mx-auto h-12 w-12 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-neutral-900">{t('workspaces.noWorkspacesFound')}</h3>
          {canCreateProject && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 transition-colors"
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('workspaces.createFirst')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project, index) => {
              const caseCount = project.cases_count || 0;
              const lastActivity = project.updated_at || project.created_at;
              const daysSinceActivity = Math.floor((new Date() - new Date(lastActivity)) / (1000 * 60 * 60 * 24));
              
              return (
                <div key={project.id} className="group relative bg-white rounded-lg border border-neutral-200 hover:border-neutral-300 hover:shadow-md transition-all duration-200">
                  {/* Status indicator */}
                  <div className={`absolute top-4 right-4 w-3 h-3 rounded-full ${
                    project.is_archived ? 'bg-neutral-400' : 
                    daysSinceActivity < 7 ? 'bg-success-dark' : 
                    daysSinceActivity < 30 ? 'bg-warning-dark' : 'bg-neutral-400'
                  }`} title={`${t('common.lastUpdated')}: ${t('workspaces.daysAgo', { days: daysSinceActivity })}`}></div>
                  
                  <Link to={`/workspaces/${project.id}`} className="block p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold ${
                          index % 5 === 0 ? 'bg-gradient-to-br from-accent-500 to-accent-600' :
                          index % 5 === 1 ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          index % 5 === 2 ? 'bg-gradient-to-br from-indigo-500 to-indigo-600' :
                          index % 5 === 3 ? 'bg-gradient-to-br from-slate-500 to-slate-600' :
                          'bg-gradient-to-br from-neutral-500 to-neutral-600'
                        }`}>
                          {project.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-3">
                          <h3 className="text-base font-semibold text-neutral-900 group-hover:text-accent-700 transition-colors">
                            {project.name}
                          </h3>
                          {project.is_archived && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700 mt-1">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                                <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                              </svg>
                              {t('common.archived')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                      {project.description || t('common.noDescription')}
                    </p>
                    
                    {/* Metadata */}
                    <div className="space-y-3">
                      {/* Framework and Time Horizon */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center text-neutral-500">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {project.category_system}
                        </div>
                        {project.time_horizon && (
                          <div className="flex items-center text-neutral-500">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {project.time_horizon}
                          </div>
                        )}
                      </div>
                      
                      {/* Stats Bar */}
                      <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
                        <div className="flex items-center space-x-4 text-xs text-neutral-500">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zm-13.5-3a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                            </svg>
                            {project.participants_count || 0}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {caseCount} {t('workspaces.cases')}
                          </div>
                        </div>
                        <svg className="w-4 h-4 text-neutral-400 group-hover:text-accent-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                  
                  {/* Action buttons */}
                  {(currentUser?.role === 'administrator' || 
                    (currentUser?.role === 'instructor' && project.creator_id === currentUser?.id)) && (
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleArchiveWorkspace(project.id, project.is_archived);
                          }}
                          className={`p-1.5 rounded border transition-colors ${
                            project.is_archived 
                              ? 'text-accent-600 border-accent-300 hover:bg-accent-50' 
                              : 'text-neutral-500 border-neutral-300 hover:bg-neutral-50'
                          }`}
                          title={project.is_archived ? t('workspaces.unarchive') : t('workspaces.archive')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                        </button>
                      
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteWorkspace(project.id, project.name);
                          }}
                          className="p-1.5 text-error-600 border border-error-300 rounded hover:bg-error-50 transition-colors"
                          title={t('common.delete')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateProject}
        />
      )}
    </div>
  );
};

export default Projects;